// @flow
import React from 'react'
import styled from 'styled-components'
import ResizeConfig from './resize-config'
import PositionConfig from './position-config'
import SliderWithInput from 'live/components/forms/ui/slider-with-input'
import FormattingConfig from './formatting-config'
import i18n from 'live/services/i18n'
import ColorDropdownOptions, {
    ColorDropdownOptionsContainer
} from 'plugin-assets/shared/components/color-dropdown-options'
import theme from 'live/services/theme'
import { FontStyleButton, ConfigSubtitle } from 'plugin-assets/shared/widget/components/styled'
import { FullHeightSelectorOpenContainer } from 'live/components/ui/full-height-selector/full-height-selector-open'

type Props = WidgetTypeActionProps & {
    changeLayoutConfig: (string, any) => void
    type: string
    dark?: boolean
    hidden: boolean
    isEditor: boolean
}
export default class ChartConfig extends React.Component<Props> {
    toggleValueBold = () => this.props.changeLayoutConfig('valueBold', !this.props.widgetConfig.valueBold)
    toggleUnitBold = () => this.props.changeLayoutConfig('unitBold', !this.props.widgetConfig.unitBold)
    toggleUnitItalic = () => this.props.changeLayoutConfig('unitItalic', !this.props.widgetConfig.unitItalic)
    toggleValueItalic = () => this.props.changeLayoutConfig('valueItalic', !this.props.widgetConfig.valueItalic)
    changeValueFontColor = (value: string) => this.props.changeLayoutConfig('valueFontColor', value)
    changeUnitFontColor = (value: string) => this.props.changeLayoutConfig('unitFontColor', value)
    changeValueFontColorOption = (value: string) => this.props.changeLayoutConfig('valueFontColorOption', value)
    changeUnitFontColorOption = (value: string) => this.props.changeLayoutConfig('unitFontColorOption', value)
    changeUnitProportion = (value: string) => this.props.changeLayoutConfig('unitProportion', value)

    render() {
        const widgetConfig = this.props.widgetConfig
        const changeLayoutConfig = this.props.changeLayoutConfig
        const config = this.props.widgetConfig

        return (
            <Padding hidden={this.props.hidden}>
                <ChartConfigBackground>
                    <PositionConfig
                        config={widgetConfig}
                        isEditor={this.props.isEditor}
                        changeLayoutConfig={changeLayoutConfig}
                    />
                    <ResizeConfig config={widgetConfig} changeLayoutConfig={changeLayoutConfig} />
                    <FormattingConfig
                        dark={this.props.dark}
                        config={widgetConfig}
                        changeLayoutConfig={changeLayoutConfig}
                    />
                    <ValueStyleContainer>
                        <ConfigSubtitle>{i18n('Value Font Color')}</ConfigSubtitle>
                        <ZIndexFixForColorDropdownOptions>
                            <ColorDropdownOptions
                                color={config.valueFontColor}
                                onChange={this.changeValueFontColor}
                                selectedOption={config.valueFontColorOption}
                                onChangeOption={this.changeValueFontColorOption}
                                position={this.props.colorPickerPosition}
                            />
                        </ZIndexFixForColorDropdownOptions>
                        <ConfigSubtitle>{i18n('Value Font Style')}</ConfigSubtitle>
                        <FontStyleButtonContainer>
                            <FontStyleButton
                                onClick={this.toggleValueBold}
                                isEditor={this.props.isEditor}
                                active={config.valueBold}
                            >
                                <i className="fas fa-bold" />
                            </FontStyleButton>
                            <FontStyleButton
                                onClick={this.toggleValueItalic}
                                isEditor={this.props.isEditor}
                                active={config.valueItalic}
                            >
                                <i className="far fa-italic" />
                            </FontStyleButton>
                        </FontStyleButtonContainer>
                    </ValueStyleContainer>
                    <UnitStyleContainer>
                        <ConfigSubtitle>{i18n('Unit Font Color')}</ConfigSubtitle>
                        <ColorDropdownOptions
                            color={config.unitFontColor}
                            onChange={this.changeUnitFontColor}
                            selectedOption={config.unitFontColorOption}
                            onChangeOption={this.changeUnitFontColorOption}
                            position={this.props.colorPickerPosition}
                        />
                        <ConfigSubtitle>{i18n('Unit proportion')}</ConfigSubtitle>
                        <SliderWithInput
                            defaultValue={config.unitProportion || 100}
                            min={10}
                            max={100}
                            stepSlider={10}
                            stepInput={1}
                            onChange={this.changeUnitProportion}
                        />
                        <ConfigSubtitle>{i18n('Unit Font Style')}</ConfigSubtitle>
                        <FontStyleButtonContainer>
                            <FontStyleButton
                                onClick={this.toggleUnitBold}
                                isEditor={this.props.isEditor}
                                active={config.unitBold}
                            >
                                <i className="fas fa-bold" />
                            </FontStyleButton>
                            <FontStyleButton
                                onClick={this.toggleUnitItalic}
                                isEditor={this.props.isEditor}
                                active={config.unitItalic}
                            >
                                <i className="far fa-italic" />
                            </FontStyleButton>
                        </FontStyleButtonContainer>
                    </UnitStyleContainer>
                </ChartConfigBackground>
            </Padding>
        )
    }
}

const Padding = styled.div`
    padding: 0px;

    ${ColorDropdownOptionsContainer} {
        background-color: ${({ theme }) => (theme.themeName === 'light' ? 'var(--gray-0)' : 'var(--gray-0)')};
        color: ${({ theme }) => (theme.themeName === 'light' ? 'var(--gray-800)' : 'var(--static-gray-300)')};
        border: 1px solid ${({ theme }) => (theme.themeName === 'light' ? 'var(--gray-0)' : 'var(--static-gray-1000)')};
        border-radius: 3px;

        div.form-color-picker__clear-button,
        div.sketch-picker {
            color: var(--static-gray-800);
            background-color: var(--static-gray-0);
            border: 1px solid var(--static-gray-200);
            border-top: 1px solid var(--static-gray-200) !important;
        }
    }
`

const ChartConfigBackground = styled.div`
    display: flex;
    flex-direction: column;
`

const FontStyleButtonContainer = styled.div`
    display: flex;
    margin: 5px 0;
    &&& > * {
        margin-right: 5px;
    }
`

const ZIndexFixForColorDropdownOptions = styled.div`
    position: relative;
    z-index: 100004;
`

export const ChannelRelativeContainer = styled.div`
    position: relative;
    margin-top: 17px;
    border-top: 1px solid ${theme['dash-background']};
    flex: 1;
    ${FullHeightSelectorOpenContainer} {
        z-index: 101001;
    }
`
export const ValueStyleContainer = styled.div`
    border-top: 1px solid ${({ theme }) => theme['gray-light']};
    padding: 0px 13px;
`

export const UnitStyleContainer = styled.div`
    margin: 17px 0px 17px 0px;
    border-top: 1px solid ${({ theme }) => theme['gray-light']};
    padding: 0px 13px 0px;
    .slider-with-value__input-container {
        padding: 0px 4px 0px 4px;
    }
`
